import { Alert, Button, Card, LoadingOverlay, Portal, Text } from "@mantine/core";
import { WarningIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { ComponentProps, useEffect } from "react";

import { OverView } from "@/components/@custom/Overview";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { QueueWarning } from "@/pages/App/Driver/DriverHome/components/QueueWarning";
import { useGetIpQuery } from "@/store/apis/ipify";
import {
  SaveCheckinArgs,
  useGetAttendancesTodayByCompanyQuery,
  useGetCheckinsCompanyActivesByCompanyQuery,
  useGetQueueByCompanyQuery,
  useGetQueueDelayStatusByCompanyQuery,
  useGetSettingsByCompanyQuery,
  useSaveCheckinMutation
} from "@/store/apis/nextcargo";
import { useSaveCheckoutLogMutation } from "@/store/apis/nextcargo/checkout/checkout.hooks";
import { SaveCheckoutLogArgs } from "@/store/apis/nextcargo/checkout/checkout.types";
import { cn } from "@/utils/cn";

export interface DriverCheckIn {
  id: number;
  driverQueueId: number;
  driverCheckInActive: boolean;
  checkInCreatedAt: string;
}

export const Route = createFileRoute("/app/")({
  component: DriverHome
});

export function DriverHome() {
  const { driver } = useSession();
  const driverId = driver?.id;
  const driverCompanyId = driver?.company.id;

  const { data: settingsCompany, isLoading: isLoadingSettings } = useGetSettingsByCompanyQuery({ id: Number(driverCompanyId)! }, { skip: !driverCompanyId });
  const { isLoading: isLoadingDriverIp } = useGetIpQuery(undefined, {
    selectFromResult: (state) => ({
      ...state,
      data: state.data?.ip
    })
  });
  const { isLoading: isLoadingAttendance } = useGetAttendancesTodayByCompanyQuery({ id: driverCompanyId! }, { skip: !driverCompanyId });
  const { isLoading: isLoadingDriverCheckIn } = useGetCheckinsCompanyActivesByCompanyQuery(
    { id: Number(driverCompanyId) },
    {
      skip: !driverCompanyId
    }
  );
  const { data: position, isLoading: isLoadingQueuePosition } = useGetQueueByCompanyQuery(
    { id: driverCompanyId! },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const driverPosition = state.data?.original.findIndex((queue) => queue?.driver.id === driverId);

        return {
          ...state,
          data: driverPosition ? driverPosition + 1 : undefined
        };
      }
    }
  );
  const { data: delay, isLoading: isLoadingDelayStatus } = useGetQueueDelayStatusByCompanyQuery(
    { id: driverCompanyId! },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        return {
          ...state,
          data: state.data?.delay?.reason
        };
      }
    }
  );

  const isLoadingPromises = isLoadingSettings || isLoadingDriverIp || isLoadingAttendance || isLoadingDriverCheckIn || isLoadingQueuePosition || isLoadingDelayStatus;
  const driverHasPosition = !!position;
  const showButtonsActions = driverHasPosition || settingsCompany?.enableQueueSave;

  const handleTerms = () => {
    MODAL_HANDLER.terms();
  };

  return (
    <>
      <Portal>
        <Alert color="red.9" icon={<WarningIcon />} title="Atenção" className="absolute! bottom-4 left-1/2 -translate-x-1/2" hidden={!delay}>
          Estamos enfrentando um atraso no atendimento. Motivo: {delay}. Agradecemos sua paciência.
        </Alert>
      </Portal>

      <div className="px-2">
        <div className="flex w-full max-w-md flex-col items-center justify-center gap-6 text-center">
          <div className="relative rounded-lg border border-gray-200 bg-white p-8 shadow-md">
            <LoadingOverlay visible={isLoadingPromises} />

            <OverView.User />

            {!isLoadingPromises && (
              <>
                {!showButtonsActions && <QueueWarning />}

                {showButtonsActions && <UserActions className="mb-4" />}
              </>
            )}

            <Button variant="transparent" className="hover:underline!" color="primary" onClick={handleTerms}>
              Política de Privacidade e Termos de Uso
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

type UserActionsProps = {} & ComponentProps<"div">;
const UserActions = ({ className, ...props }: UserActionsProps) => {
  const navigate = useNavigate();

  const { driver: driverSession } = useSession();
  const driverId = driverSession?.id;
  const driverCompanyId = driverSession?.company.id;

  const { data: driverCheckIn } = useGetCheckinsCompanyActivesByCompanyQuery(
    { id: Number(driverCompanyId) },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const activeCheckinDriver = state.data?.original.find((item) => item.queue?.driver.id === Number(driverId));
        const transformedData = {
          id: activeCheckinDriver?.id,
          driverQueueId: activeCheckinDriver?.queue.id,
          driverCheckInActive: activeCheckinDriver?.isPresent,
          checkInCreatedAt: activeCheckinDriver?.updatedAt
        };

        return {
          ...state,
          data: activeCheckinDriver ? transformedData : undefined
        };
      }
    }
  );
  const { data: position } = useGetQueueByCompanyQuery(
    { id: driverCompanyId! },
    {
      skip: !driverCompanyId,
      selectFromResult: (state) => {
        const driverPosition = state.data?.original.findIndex((queue) => queue?.driver.id === driverId);

        return {
          ...state,
          data: driverPosition !== undefined ? driverPosition + 1 : undefined
        };
      }
    }
  );
  const { data: driverIp } = useGetIpQuery(undefined, {
    selectFromResult: (state) => ({
      ...state,
      data: state.data?.ip
    })
  });
  const { data: settings } = useGetSettingsByCompanyQuery({ id: Number(driverCompanyId)! }, { skip: !driverCompanyId });

  const [saveCheckinTrigger] = useSaveCheckinMutation({ fixedCacheKey: "@save-checkin" });
  const [saveCheckoutLogTrigger] = useSaveCheckoutLogMutation({ fixedCacheKey: "@save-checkout-log" });

  const authorizedIPS = settings?.checkinIp.split(",");
  const isDriverIPAuthorized = authorizedIPS?.includes(driverIp || "");

  const handleCancelCheckIn = async () => {
    if (!driverId || !driverCheckIn?.driverCheckInActive) return;

    if (!driverCompanyId) return;

    try {
      const checkinArgs: SaveCheckinArgs = {
        id: driverCheckIn.id!,
        company: { id: driverCompanyId },
        queue: { id: driverCheckIn.driverQueueId! },
        isPresent: false,
        status: false
        // status: true
      };

      const checkoutLogArgs: SaveCheckoutLogArgs = {
        id: driverCheckIn.id!,
        driver: {
          id: driverId
        }
      };

      const promises = [saveCheckinTrigger(checkinArgs), saveCheckoutLogTrigger(checkoutLogArgs)] as const;
      const [checkinResult] = await Promise.allSettled(promises);

      if (checkinResult.status === "fulfilled") {
        navigate({
          to: "/app/checkin",
          state: (prev) => {
            return {
              ...prev,
              stepId: 4,
              checkin: {
                updatedAt: checkinResult.value.data?.updatedAt as string
              }
            };
          }
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isDriverIPAuthorized) {
      handleCancelCheckIn();
    }
  }, [isDriverIPAuthorized]);

  return (
    <div className={cn(["flex flex-col gap-2", className])} {...props}>
      {settings?.enableCheckin && (
        <>
          {isDriverIPAuthorized && !driverCheckIn && !!position && (
            <Button component={Link} color="blue.7" to="/app/checkin">
              Fazer Check-in
            </Button>
          )}
        </>
      )}

      {driverCheckIn?.driverCheckInActive && (
        <Card className="flex flex-col border-2 border-current *:bg-current *:text-current" c="blue.6" p={0}>
          <div className="p-1">
            <Text c="white" fw="bold">
              Aguarde ser Chamado
            </Text>
          </div>

          <Text bg="white" p="xs">
            Check-in realizado em {dayjs(driverCheckIn?.checkInCreatedAt).format("DD/MM [às] HH:mm")}
          </Text>
        </Card>
      )}

      {!!position && (
        <Button component={Link} to="/app/agendamento/status">
          Minha Posição
        </Button>
      )}

      {!position && (
        <Button component={Link} to="/app/fila">
          Consultar Fila
        </Button>
      )}

      <Button component={Link} to="/app/como-chegar">
        Como Chegar?
      </Button>
    </div>
  );
};
